import { FC, Fragment } from "react";
import { observer } from "mobx-react";
import { isFailure, isOk, isPending } from "@/common/common";
import { Alert } from "@/components-v2/Alert";
import { Box, VBox } from "@/components-v2/Box";
import { Button, CloseButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { CompanyRoleImportViewProps, prepCompanyRoleImportView } from "./companyRoleImportDomain";
import {
  ImportGrid,
  SpreadsheetImportFilters,
  SpreadsheetImportResultDialog,
} from "./spreadsheetImportComponents";

export const CompanyRoleImportDialog: FC = observer(() => {
  const dispatch = useDispatch();
  const state = useRootStore().companyRoleImport;
  const props = prepCompanyRoleImportView(state, dispatch);
  if (!props) {
    return null;
  }
  return props.result ? (
    <SpreadsheetImportResultDialog result={props.result} onClose={props.onClose} />
  ) : (
    <CompanyRoleImportView {...props} />
  );
});

const CompanyRoleImportView: FC<CompanyRoleImportViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <Dialog
      fullscreen
      actions={<Fragment />}
      subHeaderElement={
        <VBox>
          <Box paddingX={"md"} paddingY={"sm"} gap={"md"} alignItems={"center"} borderBottom>
            <CloseButton onClick={props.onClose} />
            <span>{t("entity-data.role-import-title")}</span>
          </Box>
          {isOk(props.setupResult) && <Toolbar {...props} />}
        </VBox>
      }
    >
      <VBox gap={"md"} grow overflow={"hidden"}>
        {isPending(props.setupResult) && (
          <Box alignItems={"center"} gap={"sm"}>
            <Spinner />
            {t("entity-data.spreadsheet-reading")}
          </Box>
        )}
        {isFailure(props.setupResult) && (
          <Alert type={"error"} message={t(props.setupResult.error)} />
        )}
        {props.importFailed && <Alert type={"error"} message={t("general-error")} />}
        {isOk(props.setupResult) && props.missingRequired.length > 0 && (
          <Alert
            type={"info"}
            message={t({
              key: "entity-data.role-import-missing",
              params: { fields: { type: "list", value: props.missingRequired } },
            })}
          />
        )}
        {isOk(props.setupResult) && <ImportGrid {...props} />}
      </VBox>
    </Dialog>
  );
});

const Toolbar: FC<CompanyRoleImportViewProps> = observer((props) => (
  <Box paddingX={"md"} paddingY={"sm"} gap={"lg"} alignItems={"center"} wrap>
    <SpreadsheetImportFilters
      {...props}
      rowsLabel={{ key: "entity-data.role-import-appointments" }}
    />
    <Box marginLeft={"auto"} alignItems={"center"} gap={"md"}>
      <Button
        variant={"primary"}
        label={{ key: "entity-data.spreadsheet-import" }}
        disabled={!props.canImport}
        loading={props.isImporting}
        onClick={props.onImport}
      />
    </Box>
  </Box>
));
